import React, { useState, useReducer, useCallback, useMemo } from "react";
import {
	Grid,
	Typography,
	Box,
	Button,
	TextField,
	MenuItem,
	Checkbox,
	FormControlLabel,
	Paper,
} from "@mui/material";
// import axios from "../../../api/axios";
import axios from "../../api/axios";
// import ShipmentInfoModal from "../../../admin_dashboard/orders/create-shipment-ui/ShipmentInfoModal";
import ShipmentInfoModal from "../../admin_dashboard/orders/create-shipment-ui/ShipmentInfoModal";
import NorthIcon from "@mui/icons-material/North";
import PersonIcon from "@mui/icons-material/Person";

// --------- Shipment reducer & initial state ---------
const initialShipments = [
	{
		itemWeight: "",
		itemLength: "",
		itemWidth: "",
		itemHeight: "",
		itemQuantity: "",
		itemValue: "",
		itemDescription: "",
		nonStacked: false,
		isFragile: false,
	},
];

function shipmentsReducer(state, action) {
	switch (action.type) {
		case "ADD_SHIPMENT":
			return [...state, { ...initialShipments[0] }];
		case "UPDATE_SHIPMENT":
			const { index, field, value } = action.payload;
			return state.map((item, i) =>
				i === index ? { ...item, [field]: value } : item
			);
		case "REMOVE_SHIPMENT":
			const newState = [...state];
			// Optional: prevent removing last shipment
			if (newState.length > 1) {
				newState.splice(action.payload.index, 1);
			}
			return newState;
		case "RESET":
			return initialShipments;
		default:
			return state;
	}
}

// --------- Main Component ---------
export default function PersonalExportShipment() {
	// Form data state
	const [formData, setFormData] = useState({
		businessName: "",
		businessEmail: "",
		receiverName: "",
		receiverEmail: "",
		fromCountry: "Nigeria",
		fromCity: "",
		fromCityPostalCode: "",
		isImport: false,
		isExport: true,
		toCountry: "China",
		isPersonal: true,
		isBusiness: false,
		toCity: "",
		toCityPostalCode: "",
	});

	const [isImport, setIsImport] = useState(false);
	const [shipments, dispatchShipments] = useReducer(
		shipmentsReducer,
		initialShipments
	);
	const [shipmentData, setShipmentData] = useState(null);
	const [open, setOpen] = useState(false);

	const fromCountryOptions = useMemo(
		() => [
			{ value: "Nigeria", label: "Nigeria", code: "NG" },
			{ value: "France", label: "France", code: "FR" },
		],
		[]
	);
	const toCountryOptions = useMemo(
		() => [{ value: "China", label: "China", code: "CN" }],
		[]
	);

	const flagIconUrl = (code) =>
		`https://flagcdn.com/w40/${code.toLowerCase()}.png`;

	// Handlers
	const handleMainChange = useCallback((e) => {
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	}, []);

	const handleShipmentChange = useCallback(
		(index, field) => (e) => {
			dispatchShipments({
				type: "UPDATE_SHIPMENT",
				payload: {
					index,
					field,
					value:
						e.target.type === "checkbox" ? e.target.checked : e.target.value,
				},
			});
		},
		[]
	);

	const handleAddShipment = useCallback(() => {
		dispatchShipments({ type: "ADD_SHIPMENT" });
	}, []);

	const handleRemoveShipment = useCallback((index) => {
		dispatchShipments({ type: "REMOVE_SHIPMENT", payload: { index } });
	}, []);

	const handleToggleChange = useCallback(() => {
		setIsImport((prev) => !prev);
	}, []);

	const handleOpen = useCallback(() => setOpen(true), []);
	const handleClose = useCallback(() => setOpen(false), []);

	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			try {
				const response = await axios.post("/create-business-export-shipment", {
					...formData,
					shipments,
					isPersonal: true,
					isBusiness: false,
					isImport: false,
					isExport: true,
				});
				if (response.status === 201) {
					setShipmentData(response.data.message);
					handleOpen();
				}
			} catch (error) {
				console.error("Submission error:", error);
			}
		},
		[formData, shipments, handleOpen]
	);

	// Utility for country options with flags
	const renderCountryOption = (option) => (
		<MenuItem
			key={option.value}
			value={option.value}
			sx={{ display: "flex", alignItems: "center" }}>
			<img
				src={flagIconUrl(option.code)}
				alt={option.label}
				width={40}
				height={30}
			/>
			<Typography ml={1}>{option.label}</Typography>
		</MenuItem>
	);

	// ------------------ Shipment Item Component ------------------
	const ShipmentItem = React.memo(({ shipment, index, onRemove }) => (
		<Box
			sx={{
				border: "1px solid #ccc",
				borderRadius: 2,
				p: 2,
				mb: 2,
				backgroundColor: "#f9f9f9",
			}}>
			{/* Header with title and cancel button */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				<Typography
					sx={{
						bgcolor: "#333",
						color: "white",
						p: 0.5,
						borderRadius: 1,
						mb: 2,
						fontSize: { xs: "0.8rem", sm: "1rem" },
					}}>
					Shipment {index + 1}
				</Typography>
				<Button
					variant="outlined"
					color="error"
					size="small"
					onClick={() => onRemove(index)}
					sx={{ ml: 1 }}>
					Cancel
				</Button>
			</Box>
			<Grid container spacing={2} alignItems="center">
				{/* Weight */}
				<Grid item xs={12} sm={6}>
					<TextField
						label="Item Weight (kg)"
						type="number"
						fullWidth
						value={shipment.itemWeight}
						onChange={handleShipmentChange(index, "itemWeight")}
						required
					/>
				</Grid>
				{/* Dimensions */}
				{["itemLength", "itemWidth", "itemHeight"].map((field) => (
					<Grid item xs={12} sm={4} key={field}>
						<TextField
							label={field.replace("item", "").replace(/([A-Z])/g, " $1")}
							type="number"
							fullWidth
							value={shipment[field]}
							onChange={handleShipmentChange(index, field)}
							required
						/>
					</Grid>
				))}
				{/* Value */}
				<Grid item xs={12} sm={6}>
					<TextField
						label="Item Value in USD"
						type="number"
						fullWidth
						value={shipment.itemValue}
						onChange={handleShipmentChange(index, "itemValue")}
						required
					/>
				</Grid>
				{/* Description */}
				<Grid item xs={12}>
					<TextField
						label="Item Description"
						placeholder="electronics, documents"
						fullWidth
						value={shipment.itemDescription}
						onChange={handleShipmentChange(index, "itemDescription")}
						required
					/>
				</Grid>
				{/* Quantity */}
				<Grid item xs={12} sm={6}>
					<TextField
						label="Item Quantity"
						type="number"
						fullWidth
						value={shipment.itemQuantity}
						onChange={handleShipmentChange(index, "itemQuantity")}
						required
					/>
				</Grid>
				{/* Checkboxes */}
				<Grid item xs={12} sm={6} display="flex" gap={1} alignItems="center">
					<FormControlLabel
						control={
							<Checkbox
								checked={shipment.nonStacked}
								onChange={handleShipmentChange(index, "nonStacked")}
								color="primary"
							/>
						}
						label="Non-stacked"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={shipment.isFragile}
								onChange={handleShipmentChange(index, "isFragile")}
								color="primary"
							/>
						}
						label="Fragile"
					/>
				</Grid>
			</Grid>
		</Box>
	));

	// ------------------ Main Render ------------------
	return (
		<Box sx={{ maxWidth: 1200, mx: "auto", px: 2, my: 4 }}>
			{/* Shipment Details Modal (if needed) */}
			{shipmentData && (
				<ShipmentInfoModal
					open={open}
					handleClose={handleClose}
					shipment={shipmentData}
				/>
			)}

			{/* Title */}
			<Typography
				variant="h4"
				align="center"
				sx={{
					mb: 3,
					p: 2,
					bgcolor: "#034052",
					color: "white",
					borderRadius: 2,
					fontSize: { xs: "1rem", md: "1.2rem" },
					fontWeight: "bold",
				}}>
				<NorthIcon /> <PersonIcon /> Personal Export
			</Typography>

			{/* Form */}
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{/* Sender & Recipient Details */}
				<Paper
					sx={{
						p: 2,
						mb: 3,
						color: "black",
						borderRadius: 2,
						fontWeight: "medium",
					}}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontSize: { xs: "0.9rem", md: "1rem" },
							fontWeight: "medium",
						}}>
						⥯ Sender & Recipient Details:
					</Typography>
					<Grid container spacing={2}>
						{/* Sender */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Business Name"
								name="businessName"
								fullWidth
								required
								value={formData.businessName}
								onChange={handleMainChange}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Business Email"
								name="businessEmail"
								type="email"
								fullWidth
								required
								value={formData.businessEmail}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* Receiver */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Receiver's Name"
								name="receiverName"
								fullWidth
								required
								value={formData.receiverName}
								onChange={handleMainChange}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Receiver's Email"
								name="receiverEmail"
								type="email"
								fullWidth
								required
								value={formData.receiverEmail}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* From Country */}
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Shipping From Country"
								value={formData.fromCountry}
								onChange={(e) =>
									handleMainChange({
										target: { name: "fromCountry", value: e.target.value },
									})
								}
								fullWidth>
								{fromCountryOptions.map(renderCountryOption)}
							</TextField>
						</Grid>
						{/* From City & Postal */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="From City"
								name="fromCity"
								fullWidth
								required
								value={formData.fromCity}
								onChange={handleMainChange}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField
								label="From Postal Code"
								name="fromCityPostalCode"
								fullWidth
								required
								value={formData.fromCityPostalCode}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* To Country */}
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Destination Country"
								value={formData.toCountry}
								onChange={(e) =>
									handleMainChange({
										target: { name: "toCountry", value: e.target.value },
									})
								}
								fullWidth>
								{renderCountryOption(toCountryOptions[0])}
							</TextField>
						</Grid>
						{/* To City & Postal */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="To City"
								name="toCity"
								fullWidth
								required
								value={formData.toCity}
								onChange={handleMainChange}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField
								label="To Postal Code"
								name="toCityPostalCode"
								fullWidth
								required
								value={formData.toCityPostalCode}
								onChange={handleMainChange}
							/>
						</Grid>
					</Grid>
				</Paper>

				{/* Shipment Details */}
				<Paper
					sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: "#f9f9f9" }}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontSize: { xs: "0.9rem", md: "1rem" },
							bgcolor: "#034052",
							color: "yellow",
							p: 1,
							borderRadius: 1,
							fontWeight: "medium",
						}}>
						❒ Shipment Details
					</Typography>
					{shipments.map((shipment, index) => (
						<ShipmentItem
							key={index}
							shipment={shipment}
							index={index}
							onRemove={handleRemoveShipment}
						/>
					))}
					{/* Buttons */}
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<Button
							variant="contained"
							color="success"
							onClick={handleAddShipment}
							sx={{ minWidth: 150 }}>
							Add Shipment
						</Button>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							sx={{ minWidth: 150 }}>
							Create Shipment
						</Button>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
}
